import React, { Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import IncomeItem from '@components/ssap/pages/pageIncludes/summary/IncomeItem';
import StudentLoanInterestHelpBanner from '@components/ssap/pages/pageIncludes/summary/StudentLoanInterestHelpBanner';
import SubHeader from '@components/ssap/headers/SubHeader';
import StudentLoanInterestAmountTooltip, {
    STUDENT_LOAN_INTEREST_AMOUNT_TOOLTIP_CONTENT,
} from '@components/ssap/tooltips/StudentLoanInterestAmountTooltip';

// Context

import { Context } from '@contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '@hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { transformDropdownOptionsToObject } from '@utils/normalize/transformDropdownOptionsToObject';
import { isEqual, pick } from '@utils/miscellaneous/objectHelpers';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EXPENSE_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';


const DeductionsSummarySection = props => {

    const DEDUCTION_SOURCES_LIST = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.DEDUCTION_SOURCES);

    const {
        applicantGuid,
        expenses,
        isFinalSummaryPage,
        isSsapInReviewMode,
        lists: {
            FREQUENCIES_LIST
        },
        previousData,
        processDate,
        hhmIndex,
        showStudentLoanInterestDeductionHelp = false
    } = props;

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const previousExpenses = useMemo(
        () => {
            const index = previousData.taxHousehold.householdMember.findIndex(hhm => hhm.applicantGuid === applicantGuid);
            if(~index) {
                const {
                    expenses
                } = previousData.taxHousehold.householdMember[index];
                return expenses;
            }
            return [];
        },
        [ previousData ]
    );

    const [
        arePreviousDeductionsShown,
        toggleDeductionsVisibility
    ] = useState(!previousExpenses.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        toggleDeductionsVisibility(isShown => !isShown);
    };

    const getDeductionAmount = (expense, idx, arePrevious = false) => {
        const formattedAmount = currencyTools.getMoneyFormat(expense.amount, true);

        if (expense.type !== EXPENSE_TYPES.STUDENT_LOAN_INTEREST) {
            return formattedAmount;
        }

        return (
            <span className='display-flex flex-align-center flex-justify-start width-full'>
                { formattedAmount }
                <StudentLoanInterestAmountTooltip
                    content={STUDENT_LOAN_INTEREST_AMOUNT_TOOLTIP_CONTENT.DEDUCTION}
                    index={ `summary-student-loan-interest-amount-${ hhmIndex }-${ idx }${ arePrevious ? '-previous' : '' }` }
                    dataAutomationId={ `summary-hhm${ hhmIndex }-student_loan_interest-amount-${ idx }${ arePrevious ? '-previous' : '' }` }
                />
            </span>
        );
    };

    const getDeductions = (deductions = [], arePrevious = false) => {
        if(deductions.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return deductions.map((expense, idx) => {
            const {
                type,
                frequency,
                incomeStartDate,
                incomeEndDate
            } = expense;

            return (
                <IncomeItem
                    key = { `${ type }_${ idx }` }
                    id={`deductions-hhm${hhmIndex}-row${idx}-col`}
                    type = { DEDUCTION_SOURCES_LIST[type] }
                    amount = { getDeductionAmount(expense, idx, arePrevious) }
                    frequency = { FREQUENCIES_LIST[frequency] }
                    incomeStartDate = { processDate(incomeStartDate) }
                    incomeEndDate = { processDate(incomeEndDate) }
                    isStrikeThroughValue = { arePrevious }
                />
            );
        });
    };

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const filterDeductionProps = (expenses = []) => {
        return expenses.map(income => {
            return pick(
                income,
                [
                    'type',
                    'sourceName',
                    'amount',
                    'frequency',
                    'incomeStartDate',
                    'incomeEndDate'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(filterDeductionProps(expenses), filterDeductionProps(previousExpenses))
            };
        },
        [ expenses, previousExpenses ]
    );

    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && previousExpenses.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    useSummaryItemDiffRegister({
        diffs: { status: expenses.length === 0 ? false : diffs.status },
        summaryContext,
        values: {
            category: 'expenses',
            value: expenses,
            previousValue: previousExpenses,
            textCode: applicantGuid
        }
    });

    if(expenses.length === 0) {
        return null;
    }

    // We highlight background only on Final Review page (SSAP in edit mode) if we are tracking a diff
    const displayUpdatedRow = (diffs.status && isFinalSummaryPage && !isSsapInReviewMode)
        ? 'bg-updated_fields'
        : '';

    return (
        <Fragment>
            <SubHeader
                { ...{
                    headerClassName: 'gi-heading-summary summary__header--no-border',
                    text: 'ssap.subheader.income_deductions',
                    dataTestId: `Income-Deductions-Header-${applicantGuid}`,
                    id: `deductions-hhm${hhmIndex}-subheader`,
                    ...linkProps
                } }
            />
            {
                showStudentLoanInterestDeductionHelp &&
                <StudentLoanInterestHelpBanner dataTestId='student-loan-interest-income-summary-help' />
            }
            <div className="grid-row gi-table__responsive">
                <table
                    className="usa-table usa-table--borderless gi-table gi-table__borderless income-table"
                    aria-labelledby={`deductions-hhm${hhmIndex}-subheader`}
                >
                    <thead>
                        <IncomeItem
                            id={`deductions-hhm${hhmIndex}-header`}
                            dataTestId = { `deductions-hhm${hhmIndex}-header` }
                            amount = 'common.how_much_without_question_mark'
                            design = 'header'
                            frequency = 'common.how_often_without_question_mark'
                            incomeStartDate = 'common.start_date'
                            incomeEndDate = 'common.end_date'
                            type = 'common.deduction_source'
                        />
                    </thead>
                    <tbody>
                        <div className = { displayUpdatedRow } >
                            {
                                arePreviousDeductionsShown && getDeductions(previousExpenses, true)
                            }
                            {
                                getDeductions(expenses)
                            }
                        </div>
                    </tbody>
                </table>
            </div>
        </Fragment> 
    );
};

export default DeductionsSummarySection;